import React from 'react';
import { Zap, Image, Package, Trash2, FileCode } from 'lucide-react';
import { BuildSettings } from '../../../../core/types/build.types';

interface Props {
  settings: BuildSettings;
  onUpdate: (settings: Partial<BuildSettings>) => void;
}

export const OptimizationOptions: React.FC<Props> = ({ settings, onUpdate }) => {
  const options = [
    {
      key: 'minifyJs' as const,
      label: 'Minify JavaScript',
      description: 'Reduce file size by removing whitespace and shortening variable names',
      icon: Zap,
      value: settings.minifyJs,
    },
    {
      key: 'compressImages' as const,
      label: 'Compress Images',
      description: 'Optimize image files for smaller file sizes',
      icon: Image,
      value: settings.compressImages,
    },
    {
      key: 'bundleAssets' as const,
      label: 'Bundle Assets',
      description: 'Combine multiple files into optimized bundles',
      icon: Package,
      value: settings.bundleAssets,
    },
    {
      key: 'removeUnused' as const,
      label: 'Remove Unused Assets',
      description: 'Exclude assets not referenced in any scene',
      icon: Trash2,
      value: settings.removeUnused,
    },
    {
      key: 'generateSourceMaps' as const,
      label: 'Generate Source Maps',
      description: 'Enable debugging in production builds (increases size)',
      icon: FileCode,
      value: settings.generateSourceMaps,
      devOnly: true,
    },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-zinc-300">Optimization</h3>

      <div className="space-y-2">
        {options.map((option) => (
          <label
            key={option.key}
            className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors
              ${option.value ? 'bg-blue-600/10 border border-blue-500/30' : 'bg-zinc-800/50 border border-zinc-700 hover:bg-zinc-800'}
              ${option.devOnly && settings.configuration === 'production' ? 'opacity-50' : ''}`}
          >
            <input
              type="checkbox"
              checked={option.value}
              onChange={(e) => onUpdate({ [option.key]: e.target.checked })}
              disabled={option.devOnly && settings.configuration === 'production'}
              className="mt-1 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-blue-600 focus:ring-blue-500"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <option.icon size={14} className={option.value ? 'text-blue-400' : 'text-zinc-500'} />
                <span className={`text-sm font-medium ${option.value ? 'text-blue-300' : 'text-zinc-300'}`}>
                  {option.label}
                </span>
                {option.devOnly && (
                  <span className="px-1.5 py-0.5 text-xs bg-yellow-600/30 text-yellow-400 rounded">
                    Dev only
                  </span>
                )}
              </div>
              <p className="text-xs text-zinc-500 mt-0.5">{option.description}</p>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
};
